"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, Landmark, FileCheck2, Scale, ShieldCheck, ChartPie as PieChart, Calculator, ClipboardCheck, Coins, BriefcaseBusiness, FileText, HardHat, ArrowRight, ChevronRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
interface GovernmentService {
  id: string;
  title: string;
  description: string;
}
const servicesList: GovernmentService[] = [{
  id: "contabilidad-gubernamental",
  title: "Contabilidad gubernamental",
  description: "Registro sistemático del ejercicio presupuestal y patrimonial conforme a la normatividad vigente."
}, {
  id: "armonizacion-conac",
  title: "Armonización contable y CONAC",
  description: "Adecuación integral de catálogos y emisión de estados financieros bajo lineamientos del CONAC."
}, {
  id: "tesoreria-municipal",
  title: "Tesorería municipal",
  description: "Optimización de flujos operativos, cobranza local y conciliación de cuentas públicas."
}, {
  id: "ley-ingresos",
  title: "Ley de ingresos",
  description: "Estructuración técnica de iniciativas y estimaciones de captación tributaria y derechos."
}, {
  id: "presupuesto-egresos",
  title: "Presupuesto de egresos",
  description: "Asignación analítica de recursos por programas presupuestarios y partidas específicas."
}, {
  id: "planeacion-presupuestacion",
  title: "Planeación y presupuestación",
  description: "Alineación de metas operativas con el Plan Municipal de Desarrollo e indicadores de desempeño."
}, {
  id: "disciplina-financiera",
  title: "Disciplina financiera",
  description: "Vigilancia de techos de gasto, sostenibilidad de deuda y reglas de responsabilidad hacendaria."
}, {
  id: "cuenta-publica",
  title: "Cuenta pública",
  description: "Integración documental y cierre anual ordenado para entrega oportuna a órganos fiscalizadores."
}, {
  id: "recursos-federales",
  title: "Recursos federales",
  description: "Supervisión del ejercicio, comprobación documental y reporte periódico ante plataformas oficiales."
}, {
  id: "participaciones-aportaciones",
  title: "Participaciones y aportaciones",
  description: "Verificación de fórmulas de ministración y destino normativo de fondos del Ramo 33 y 28."
}, {
  id: "control-interno",
  title: "Control interno",
  description: "Diagnóstico y diseño de matrices de riesgo para salvaguardar el patrimonio institucional."
}, {
  id: "fiscalizacion",
  title: "Fiscalización",
  description: "Atención preventiva y solventación metódica de pliegos de observaciones de entes superiores."
}, {
  id: "auditoria-gubernamental",
  title: "Auditoría gubernamental",
  description: "Revisión técnica de expedientes de gasto para asegurar congruencia legal y contable."
}, {
  id: "adquisiciones-servicios",
  title: "Adquisiciones, arrendamientos y servicios",
  description: "Dictaminación de bases de licitación, comités de compras y formalización contractual."
}, {
  id: "obra-publica",
  title: "Obra pública",
  description: "Integración de expedientes unitarios, estimaciones, finiquitos y actas de entrega-recepción."
}, {
  id: "responsabilidades-administrativas",
  title: "Responsabilidades administrativas",
  description: "Acompañamiento a servidores públicos en procedimientos de investigación y responsabilidades."
}];
export default function GovernmentServices() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.04,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 12
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="servicios-para-gobierno" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden border-b border-border">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        
        {/* Top Header Block */}
        <div className="mb-14 lg:mb-18 max-w-3xl">
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center justify-center size-8 rounded-sm bg-muted text-foreground border border-border">
              <Landmark className="size-4 text-ring" />
            </span>
            <Badge variant="outline" className="text-[11px] font-serif uppercase tracking-[0.15em] border-border text-foreground px-3 py-1 font-semibold rounded-sm bg-card">
              Administración Pública y Órganos Autónomos
            </Badge>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground uppercase">
            Gobierno
          </h2>

          {/* ASV Gold Hairline Accent */}
          <div className="w-12 h-[2px] bg-ring mt-4 mb-5" />

          <p className="font-sans text-base lg:text-lg text-muted-foreground leading-relaxed">
            Acompañamiento especializado para municipios, entidades estatales y organismos descentralizados. Asegure disciplina presupuestaria, cumplimiento con el CONAC y solvencia técnica en cada proceso de fiscalización.
          </p>
        </div>

        {/* 2-Column Main Structure */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          
          {/* Left Column: Visual Briefing & Trust Anchor */}
          <div className="lg:col-span-4 space-y-6 lg:sticky lg:top-24">
            <div className="relative rounded-sm overflow-hidden border border-border shadow-md bg-card group">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/mexican_council_working_session.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/mexican_council_working_session.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/mexican_council_working_session.png-wvc-sizes" alt="Sesión de cabildo y trabajo de administración municipal en México" loading="lazy" decoding="async" className="w-full h-72 sm:h-84 lg:h-96 object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/30 to-transparent flex flex-col justify-end p-6 text-primary-foreground">
                <span className="text-xs font-serif uppercase tracking-widest text-accent font-semibold mb-1">
                  Enfoque Institucional
                </span>
                <p className="font-serif text-sm font-medium leading-snug">
                  Auditoría, legalidad y control contable en cabildos y dependencias públicas.
                </p>
              </div>
            </div>

            {/* Credibility Snapshot Card */}
            <div className="border border-border bg-card p-6 rounded-sm shadow-xs space-y-4">
              <div className="flex items-center gap-3">
                <div className="size-2 rounded-full bg-ring" />
                <span className="font-serif text-xs uppercase tracking-wider font-semibold text-foreground">
                  Estrategia · Cumplimiento · Resultados
                </span>
              </div>
              <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                Supervisión directa por auditores y especialistas senior para prevenir observaciones de órganos fiscalizadores y asegurar transparencia en el ejercicio del gasto.
              </p>

              <div className="pt-2 border-t border-border flex flex-col gap-3">
                <Button asChild className="w-full justify-between bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm text-xs font-serif tracking-wider uppercase h-11 px-4 shadow-xs">
                  <Link to="/ContactPage">
                    <span>Solicitar Asesoría</span>
                    <ArrowRight className="size-4 text-ring" />
                  </Link>
                </Button>

                <Button asChild variant="outline" className="w-full justify-between border-border bg-background hover:bg-muted text-foreground rounded-sm text-xs font-serif tracking-wider uppercase h-11 px-4">
                  <Link to="/OnlineConsultingPage">
                    <span>Hablar con un Especialista</span>
                    <ChevronRight className="size-4 text-muted-foreground" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Right Column: Exhaustive Services Grid */}
          <div className="lg:col-span-8">
            <div className="border-b border-border pb-4 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <span className="font-serif text-xs uppercase tracking-[0.12em] font-semibold text-muted-foreground">
                Catálogo Operativo de Servicios Públicos
              </span>
              <span className="text-xs font-mono text-muted-foreground">
                16 áreas de intervención técnica
              </span>
            </div>

            <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
            once: true,
            amount: 0.1
          }} className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              {servicesList.map((service, index) => <motion.div key={service.id} data-index={index} variants={itemVariants} className="group relative bg-card border border-border hover:border-ring/50 p-5 sm:p-6 rounded-sm shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-200 flex flex-col justify-between">
                  {/* Subtle top hairline */}
                  <div className="absolute top-0 left-0 right-0 h-[2px] bg-transparent group-hover:bg-ring transition-colors duration-200" />
                  
                  <div>
                    <div className="flex items-start gap-3 mb-2.5">
                      <span className="size-2 rounded-full bg-ring mt-1.5 shrink-0" />
                      <h3 className="font-serif text-sm sm:text-base font-semibold text-foreground uppercase tracking-wide leading-snug">
                        {service.title}
                      </h3>
                    </div>
                    <p className="font-sans text-xs sm:text-sm text-muted-foreground leading-relaxed pl-5">
                      {service.description}
                    </p>
                  </div>

                  <div className="mt-4 pt-3 border-t border-border/60 pl-5 flex items-center justify-between">
                    <span className="text-[11px] font-mono text-muted-foreground/80">
                      ASV-GOB-{String(index + 1).padStart(2, "0")}
                    </span>
                    <Link to="/ContactPage" className="text-xs font-serif uppercase tracking-wider font-semibold text-foreground inline-flex items-center gap-1 group-hover:text-ring transition-colors">
                      Consultar
                      <ChevronRight className="size-3 text-ring" />
                    </Link>
                  </div>
                </motion.div>)}
            </motion.div>

            {/* Bottom Support Information Notice */}
            <div className="mt-8 p-5 bg-muted border border-border rounded-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <ShieldCheck className="size-5 text-ring shrink-0" />
                <p className="font-sans text-xs text-foreground leading-normal">
                  Todas nuestras consultorías se estructuran bajo las leyes vigentes de Coordinación Fiscal, Contabilidad Gubernamental y Disciplina Financiera.
                </p>
              </div>
              <Button asChild size="sm" variant="outline" className="shrink-0 text-xs font-serif uppercase border-border hover:bg-background">
                <Link to="/TendersPage">
                  Licitaciones Públicas
                </Link>
              </Button>
            </div>

          </div>

        </div>

      </div>
    </section>;
}